import { Alert, App as AntApp, Button, Card, DatePicker, Form, Input, Modal, Space, Switch, Table, Tag, Typography } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { exitKeys, useLeavingReasons, type LeavingReason, type LeavingReasonCount } from "../app/exits.ts";

const nameMaxLength = 200;

/** The school's leaving reasons (managed by people.manage) and how many students left for each over a period. */
export function LeavingReasonsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("people.manage");
  const reasons = useLeavingReasons();
  const [editing, setEditing] = useState<LeavingReason | "new" | undefined>(undefined);

  const columns = [
    { key: "name", title: t("leavingReasons.columns.name"), dataIndex: "name" },
    {
      key: "status",
      title: t("leavingReasons.columns.status"),
      width: 120,
      render: (_: unknown, r: LeavingReason) => <Tag color={r.isActive ? "green" : "default"}>{r.isActive ? t("leavingReasons.active") : t("leavingReasons.inactive")}</Tag>,
    },
    ...(canManage
      ? [
          {
            key: "actions",
            title: t("leavingReasons.columns.actions"),
            width: 120,
            render: (_: unknown, r: LeavingReason) => (
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(r)} aria-label={t("leavingReasons.editNamed", { name: r.name })}>
                {t("leavingReasons.edit")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("leavingReasons.title")}
        </Typography.Title>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("leavingReasons.create")}
          </Button>
        )}
      </div>
      <Typography.Text type="secondary">{t("leavingReasons.hint")}</Typography.Text>
      {reasons.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void reasons.refetch()}>{t("app.retry")}</Button>} />}
      <Table<LeavingReason>
        rowKey="id"
        size="middle"
        loading={reasons.isPending}
        columns={columns}
        dataSource={reasons.data ?? []}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: t("leavingReasons.empty") }}
      />
      <ExitCountsCard />
      {canManage && <ReasonModal reason={editing} onClose={() => setEditing(undefined)} />}
    </Space>
  );
}

function ExitCountsCard() {
  const { t } = useTranslation();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(1, "year").add(1, "day"), dayjs()]);
  const from = range[0].format("YYYY-MM-DD");
  const to = range[1].format("YYYY-MM-DD");

  const counts = useQuery({
    queryKey: exitKeys.counts(from, to),
    queryFn: async () => {
      const { data, error } = await api.GET("/leaving-reasons/counts", { params: { query: { From: from, To: to } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const total = (counts.data ?? []).reduce((sum, c) => sum + Number(c.count), 0);
  return (
    <Card size="small" title={t("leavingReasons.counts.title")} extra={t("leavingReasons.counts.total", { count: total })}>
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <DatePicker.RangePicker
          value={range}
          format="DD.MM.YYYY"
          allowClear={false}
          aria-label={t("leavingReasons.counts.range")}
          onChange={(values) => {
            if (values?.[0] && values[1]) setRange([values[0], values[1]]);
          }}
        />
        {counts.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void counts.refetch()}>{t("app.retry")}</Button>} />}
        <Table<LeavingReasonCount>
          rowKey="reasonId"
          size="small"
          loading={counts.isPending}
          dataSource={counts.data ?? []}
          pagination={false}
          locale={{ emptyText: t("leavingReasons.empty") }}
          columns={[
            {
              key: "reason",
              title: t("leavingReasons.columns.name"),
              render: (_, c) => (c.isActive ? c.reasonName : t("leavingReasons.inactiveNamed", { name: c.reasonName })),
            },
            { key: "count", title: t("leavingReasons.counts.count"), dataIndex: "count", width: 120, align: "right" as const },
          ]}
        />
      </Space>
    </Card>
  );
}

interface ReasonForm {
  name?: string;
  isActive?: boolean;
}

function ReasonModal({ reason, onClose }: { reason: LeavingReason | "new" | undefined; onClose(): void }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ReasonForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const existing = reason !== undefined && reason !== "new" ? reason : undefined;

  useEffect(() => {
    if (reason !== undefined) {
      form.resetFields();
      form.setFieldsValue(existing ? { name: existing.name, isActive: existing.isActive } : { name: "", isActive: true });
      setSummary([]);
    }
  }, [reason, existing, form]);

  const save = useMutation({
    mutationFn: async (values: ReasonForm) => {
      const name = values.name ?? "";
      const result = existing
        ? await api.PUT("/leaving-reasons/{id}", { params: { path: { id: existing.id } }, body: { name, isActive: values.isActive ?? true } })
        : await api.POST("/leaving-reasons", { body: { name } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: exitKeys.reasons }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t("leavingReasons.saved"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  return (
    <Modal
      open={reason !== undefined}
      title={existing ? t("leavingReasons.editTitle") : t("leavingReasons.createTitle")}
      okText={t("family.save")}
      cancelText={t("family.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Form<ReasonForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate(values)}>
        <Form.Item name="name" label={t("leavingReasons.columns.name")} rules={[{ required: true, whitespace: true, message: t("family.validation.required") }]}>
          <Input maxLength={nameMaxLength} autoFocus />
        </Form.Item>
        {existing && (
          <Form.Item name="isActive" label={t("leavingReasons.columns.status")} valuePropName="checked" extra={t("leavingReasons.deactivateHint")}>
            <Switch checkedChildren={t("leavingReasons.active")} unCheckedChildren={t("leavingReasons.inactive")} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
